import { Button, message, Popconfirm } from 'antd';
import { useTranslate } from '@tolgee/react';
import { confirmReturn, ConfirmReturnRequest } from 'services/api/return';
import posthog from '@leyan/lytics';
import { ButtonProps } from 'antd/es/button';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { formatNumber } from 'utils/numberFormat';
import { useRef, useState } from 'react';

/** 全额退款; 接受=接受仅退款申请; */
const Confirm = (
  props: ButtonProps &
    ConfirmReturnRequest & {
      unit?: string;
      refundAmount?: number;
      platform?: Omit<General.PlatformUpperCase, 'TOKOPEDIA' | 'ALIEXPRESS'>;
      returnOrderItemId?: string[];
      onSuccess?: () => void;
    },
) => {
  const { t } = useTranslate();
  const {
    externalStoreId,
    returnSn,
    externalTradeId,
    operate,
    unit,
    refundAmount,
    platform,
    returnOrderItemId,
    onSuccess,
    disabled,
    onClick: onClickProp,
    ...btnProps
  } = props;
  const sysLang = useSystemLanguageValue();
  const [visible, setVisible] = useState(false);
  const preventDefault = useRef(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClickProp?.(e);
    if (e.defaultPrevented) {
      preventDefault.current = true;
      return;
    }
    setVisible(true);
  };

  const handleVisibleChange = (visible: boolean) => {
    if (preventDefault.current) {
      preventDefault.current = false;
      return;
    }
    setVisible(visible);
  };

  const confirm = () => {
    setVisible(false);
    const reqData = {
      externalStoreId,
      externalTradeId,
      returnSn,
      operate,
      returnOrderItemId,
      externalType: platform,
    };
    confirmReturn(reqData)
      .then(() => {
        posthog.capture('$returnRst', {
          custRreport: {
            opType: operate,
            status: 'ok',
            reqData,
          },
        });
        message.success(t('ViewRefund.已全额退款'));
        onSuccess?.();
      })
      .catch((e) => {
        posthog.capture('$returnRst', {
          custRreport: {
            opType: operate,
            status: 'err',
            errTip: e.message,
            reqData,
          },
        });
        if (e.response.data.code === 1029) {
          try {
            const json = JSON.parse(e.response.data.msg);
            const msg = json[sysLang];
            message.error(msg);
          } catch (error) {
            message.error(t('trade.同意争议操作失败'));
          }
        } else {
          message.error(getApiErrorCodeMsg(e.response));
        }
      });
  };

  if (platform === 'LAZADA') {
    return (
      <Popconfirm
        visible={visible}
        onVisibleChange={handleVisibleChange}
        disabled={disabled}
        title={t('ViewRefund.确认同意买家的退款申请吗？将退款给买家：x', {
          x: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
        })}
        onConfirm={confirm}
      >
        <Button size="small" disabled={disabled} {...btnProps} onClick={handleClick}>
          {t('ViewRefund.仅退款')}
        </Button>
      </Popconfirm>
    );
  }

  let btnText = t('trade.接受');

  if (operate === 'FULL_REFUND') {
    if (isTiKTokUpperCase(platform)) {
      btnText = t('btn_agree_only_refund');
    } else {
      btnText = t('trade.全额退款');
    }
  }
  return (
    <Popconfirm
      visible={visible}
      onVisibleChange={handleVisibleChange}
      disabled={disabled}
      title={
        operate === 'FULL_REFUND'
          ? t('trade.全额退款提示', {
              money: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
            })
          : t('trade.接受提示')
      }
      onConfirm={confirm}
    >
      <Button size="small" disabled={disabled} {...btnProps} onClick={handleClick}>
        {btnText}
      </Button>
    </Popconfirm>
  );
};

export default Confirm;
